import React, { useState, useRef, useEffect } from 'react';
import { 
  Input, 
  Button, 
  Typography,
  Avatar,
  Spin,
  Layout,
  Breadcrumb,
  Badge
} from 'antd';
import logo from '../assets/logo.png';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Content } = Layout;

const LLMObservabilityPage = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Auto-scroll when messages change or bot is typing
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSend = () => {
    if (!input.trim()) return;
    
    const userMessage = {
      type: 'user',
      content: input
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      setIsLoading(false);
      setIsTyping(true);
      
      // Simulate typing effect
      setTimeout(() => {
        const botMessage = {
          type: 'bot',
          content: `You asked: "${input}"`
        };
        
        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);
      }, 1500);
    }, 800);
  };

  // Message component to display chat messages
const Message = ({ type, content }) => {
    const isBot = type === 'bot';
    
    return (
      <div style={{ 
        display: 'flex', 
        marginBottom: 16,
        alignItems: 'flex-start',
        justifyContent: isBot ? 'flex-start' : 'flex-end',
        width: '100%'
      }}>
        {isBot && (
          <Avatar style={{ 
              backgroundColor: '#1890ff',
              marginRight: '12px',
              marginTop: '4px'
            }} 
          />
        )}
        
        <div style={{ 
          maxWidth: '70%',
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: isBot ? '#f0f7ff' : '#f6ffed',
          borderTopLeftRadius: isBot ? '0' : '8px',
          borderTopRightRadius: isBot ? '8px' : '0',
        }}>
          <Text strong style={{ display: 'block', marginBottom: '4px', color: isBot ? '#1890ff' : '#52c41a' }}>
            {isBot ? 'LLM Observer' : 'You'}
          </Text>
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {content}
          </div>
        </div>
        
        {!isBot && (
          <Avatar style={{ 
              backgroundColor: '#52c41a',
              marginLeft: '12px',
              marginTop: '4px'
            }} 
          />
        )}
      </div>
    );
  };

  return (
    <div 
      style={{ 
        width: '1207px',
        height: '869px',
        position: 'fixed',
        top: '98px',
        left: '233px',
        transform: 'rotate(0deg)',
        opacity: 1,
        overflow: 'hidden',
        backgroundColor: 'transparent'
      }}
    >
      {/* Welcome Message - only shown when no messages */}
      {messages.length === 0 && (
        <>
          {/* Logo Container */}
          <div style={{ 
            position: 'absolute',
            width: '278px',
            height: '100px', // Increased height to accommodate both text lines
            top: '60px',
            left: '464px',
            gap: '11px',
            transform: 'rotate(0deg)',
            opacity: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start'
          }}>
            {/* Logo */}
            <img 
              src={logo}
              alt="LLM Observability Logo"
              style={{ 
                width: '110px',
                height: '96px',
                transform: 'rotate(0deg)',
                opacity: 1,
                marginBottom: '11px'
              }} 
            />
            
            {/* Title text */}
            <div style={{ 
              width: '400px',
              height: '29px',
              transform: 'rotate(0deg)',
              opacity: 1,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '4px'
            }}>
              <Text style={{ 
                fontSize: '14px',
                color: '#666',
                fontWeight: '600',
                fontFamily: 'Söhne, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif'
              }}>
                Observability Accelerator
              </Text>
            </div>
            
            {/* Subtitle text */}
            <div style={{ 
              width:'412px',
              marginBottom: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Text style={{ 
                fontSize: '12px',
                color: '#999',
                fontFamily: 'Söhne, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif'
              }}>
                Monitor and analyze your language model performance
              </Text>
            </div>
          </div>
        </>
      )}

      {/* Chat Messages Area */}
      <div 
        ref={chatContainerRef}
        style={{ 
          position: 'absolute',
          top: '200px',
          left: '0',
          right: '0',
          bottom: '180px', // Increased bottom margin to prevent overlap with input
          overflowY: 'auto',
          padding: '16px',
          maxHeight: '300px' // Fixed maximum height to ensure messages stay within bounds
        }}
      >
        {/* Chat Messages */}
        {messages.map((message, index) => (
          <Message 
            key={index} 
            type={message.type} 
            content={message.content} 
          />
        ))}
        
        {isTyping && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: 16,
            flexDirection: 'row'
          }}>
            <Avatar style={{ 
                backgroundColor: '#1890ff',
                marginRight: '12px',
                marginTop: '4px',             
              }} 
            />
            <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: '#f0f7ff' }}>
              <Spin size="small" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Box - Positioned as per specifications */}
      <div
        style={{          
          position: 'absolute',
          width: '600px',
          height: '56px',
          top: '520px', // Moved down slightly to create better separation
          left: '269px',
          transform: 'rotate(0deg)',
          opacity: 1,
          borderRadius: '81px',
          backgroundColor: '#F0F0F0',
          border: '1px solid #d9d9d9',
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '20px',
          paddingRight: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Input
          ref={inputRef}
          placeholder="How can I help with your LLM monitoring?"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          style={{ 
            border: 'none',
            fontSize: '16px',
            background: 'transparent',
            outline: 'none',
            boxShadow: 'none',
            flex: 1,
            height: '100%'
          }}
        />
      </div>
      
      {/* Send Button - Positioned beside input box */}
      <Button
        onClick={handleSend}
        disabled={!input.trim() || isLoading}
        type="primary"
        style={{ 
          position: 'absolute', // Added position absolute to ensure proper positioning
          width: '56px',
          height: '56px',
          top: '520px', // Updated to match input position
          left: '883px', 
          opacity: 1,
          borderRadius: '81px',
          backgroundColor: '#F0F0F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        
        }}/>
    </div>
  );
};

export default LLMObservabilityPage;